import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";

import config from '@/config';
import ProfileDataContext from '@/context/ProfileDataContext';
import useGetSearchParams from "@/hooks/useGetSearchParams";
import useNavigateToArtwork from "@/hooks/useNavigateToArtwork";
import useQuery from "@/hooks/useQuery";
import ProfileAddButton from '@/shared-components/buttons/components/ProfileAddButton';
import ProfileContentLoader from '@/shared-components/loader/special-loaders/ProfileContentLoader';
import OrderByPopup from '@/shared-components/popups/components/OrderByPopup';
import Text from '@/shared-components/text/components/Text';

import ProfileArtworkDrafts from "../artworks-drafts";
import PopUpCreateOptions from "../drafts/components/PopUpCreateOptions.jsx";
import PopUpDrafts from "../drafts/components/PopUpDrafts";
import MetaDescripProfilePages from "../MetaDescripProfilePages";

import VirtualList from './components/VirtualList';

import './index.css';

export default function ProfileArtworks() {
  const { profileData, internal } = useContext(ProfileDataContext);
  const navigate = useNavigate();
  const [params, setParams] = useGetSearchParams({ validParams: ["sort", "user_type"] });
  const [query, setQuery] = useState(params || { sort: "Recent", user_type: profileData.user_type });
  const { loading, queryData, loadMoreData } = useQuery(
    `${profileData._id}_artworks`,
    `${config.apis.api.url}/artworks/${profileData._id}`,
    query,
    {
      invalidateWhen: internal
        ? ["BUY_ARTWORK", "EDIT_ARTWORK", "DELETE_ARTWORK", `${profileData._id}_REFRESH`]
        : [`${profileData._id}_REFRESH`],
    }
  );
  const navigateToArtwork = useNavigateToArtwork(
    `${profileData._id}_artworks`,
    queryData?.data,
    `/artworks/${profileData._id}${queryData?.queryString}`
  );

  function selectSortOption(option) {
    const newQuery = { ...query, sort: option };
    setQuery(newQuery);
    setParams(newQuery, { replace: true });
  }

  const defaultPopUpValue = false;
  const [openOptions, setOpenOptions] = useState(defaultPopUpValue);
  const [openDraftPopup, setOpenDraftPopup] = useState(false);

  return (
    <>
      <MetaDescripProfilePages fetchData={profileData} subPage="artworks" />

      {internal && profileData.user_type === "gallery" && (
        <>
          <ProfileAddButton
            onClick={() =>
              profileData?.draft_count === 0
                ? navigate("/profile/add-artwork")
                : setOpenOptions(!openOptions)
            }
          />

          <PopUpCreateOptions
            open={openOptions}
            close={() => setOpenOptions(defaultPopUpValue)}
            openDraftPopup={setOpenDraftPopup}
            textOption="Artwork"
            to="/profile/add-artwork"
          />

          <PopUpDrafts
            open={openDraftPopup}
            close={() => setOpenDraftPopup(false)}
            DraftsList={ProfileArtworkDrafts}
          />
        </>
      )}

      {(!queryData || (queryData?.data.data.length === 0 && loading)) ? (
        <div className="profile-artworks-loader-container">
          <ProfileContentLoader />
        </div>
      ) : queryData?.data.data.length === 0 && !loading ? (
        <Text className="user-profile__artworks-empty-view" paragraph small>
          {internal ? (
            profileData.user_type === "gallery" ? (
              "Your available artworks will build this view"
            ) : (
              "Your artworks uploaded by galleries will build this view"
            )
          ) : (
            "No works available at the moment."
          )}
        </Text>
      ) : (
        <>
          <OrderByPopup
            defaultOption={query.sort}
            options={config.search.profile.artwork_sort_options}
            onChangeOption={selectSortOption}
          />

          {loading ? (
            <div className="profile-artworks-loader-container">
              <ProfileContentLoader />
            </div>
          ) : (
            <VirtualList
              key={queryData.queryString}
              items={queryData.data}
              onLoadMore={loadMoreData}
              navigateToArtwork={navigateToArtwork}
            />
          )}
        </>
      )}
    </>
  );
}
